import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import {
    Search, ChevronRight, FileText, Users, GraduationCap
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const thisMonth = () => new Date().toISOString().slice(0, 7);

export default function SigurimiDashboard() {
    const nav = useNavigate();
    const [kind, setKind] = useState("student");
    const [people, setPeople] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [todayCount, setTodayCount] = useState(0);
    const [monthCount, setMonthCount] = useState(0);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/sigurimi/people", { params: { kind, ...(search ? { search } : {}) } });
                setPeople(r.data || []);
            } finally { setLoading(false); }
        })();
    }, [kind, search]);

    useEffect(() => {
        (async () => {
            try {
                const month = thisMonth();
                const [t, m] = await Promise.all([
                    api.get("/sigurimi/incidents", { params: { date_from: today(), date_to: today() } }),
                    api.get("/sigurimi/incidents", { params: { month } }),
                ]);
                setTodayCount((t.data || []).length);
                setMonthCount((m.data || []).length);
            } catch { /* ignore */ }
        })();
    }, []);

    return (
        <Layout>
            <div className="mb-5">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Sigurimi</div>
                <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Regjistrim Incidentesh</h1>
                <p className="mt-1 text-slate-600 text-xs sm:text-sm">Zgjidh nxënësin ose mësuesin → hap faqen për incidentin. Raportet i shohin vetëm Drejtoresha dhe Administrata.</p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-5">
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-center" data-testid="sig-stat-today">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Sot</div>
                    <div className="text-2xl font-heading font-black text-oxford">{todayCount}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-3 text-center" data-testid="sig-stat-month">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Këtë muaj</div>
                    <div className="text-2xl font-heading font-black text-oxford">{monthCount}</div>
                </div>
                <Link to="/sigurimi/reports" className="bg-oxford text-white rounded-xl p-3 text-center hover:bg-oxfordhover transition flex flex-col items-center justify-center gap-1" data-testid="sig-stat-reports">
                    <FileText className="w-5 h-5" />
                    <div className="text-[10px] uppercase tracking-widest">Raportet</div>
                </Link>
            </div>

            <div className="flex items-center justify-center gap-2 mb-4" data-testid="sig-kind-toggle">
                <button onClick={() => setKind("student")}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition border-2 ${kind === "student" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 text-slate-700"}`}
                    data-testid="sig-kind-student">
                    <GraduationCap className="w-5 h-5" /> Nxënës
                </button>
                <button onClick={() => setKind("teacher")}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition border-2 ${kind === "teacher" ? "bg-oxford text-white border-oxford" : "bg-white border-slate-200 text-slate-700"}`}
                    data-testid="sig-kind-teacher">
                    <Users className="w-5 h-5" /> Mësues
                </button>
            </div>

            <div className="relative mb-4">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Kërko ${kind === "student" ? "nxënës" : "mësues"}…`} className="pl-9 h-12 text-base" data-testid="sig-search" />
            </div>

            <div className="space-y-2" data-testid="sig-people-list">
                {loading ? <div className="text-sm text-slate-500">Duke ngarkuar…</div> :
                 people.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500 text-sm">Asnjë rezultat.</div> : (
                    people.map((p) => (
                        <button key={p.id} onClick={() => nav(`/sigurimi/incident/${kind}/${p.id}`)}
                            className="w-full bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 hover:border-oxford transition text-left active:bg-bone"
                            data-testid={`sig-person-${p.id}`}>
                            <div className={`w-11 h-11 rounded-xl ${kind === "student" ? "bg-oxford/10 text-oxford" : "bg-sage/15 text-sage"} flex items-center justify-center font-bold text-sm shrink-0`}>
                                {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-slate-900 truncate">{p.name}</div>
                                <div className="text-[11px] text-slate-500 truncate">
                                    {kind === "student" ? (p.class_name || "Pa klasë") : (p.email || "—")}
                                </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                        </button>
                    ))
                )}
            </div>
        </Layout>
    );
}
